/**
 * TeamShareBar — the first row of the player drawer in a team game
 * (docs/team-games/team-shapes.html §3): one segment per team sized by its
 * share of the measure, each labelled with the team's shape, letter and value,
 * and a one-line summary ("Team A leads by 3 · you are Team A"). A small
 * control switches the measure between territories, troops and income
 * (next-turn reinforcements).
 *
 * Segments use the neutral team tints, not player colours: a segment stands
 * for the whole team. The tints are light, so the labels use dark ink in both
 * themes.
 */
import React, { useState } from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import { TEAM_IDS, teamLabel, type TeamId } from '@warcarp/api-types'
import { TEAM_TINTS, TEAM_TINT_INK } from '../../lib/teamShapes'
import { teamShareSummary, type TeamShare, type TeamShareMeasure } from '../../lib/teamPlay'
import { TeamShape } from './TeamShape'

const MEASURE_LABEL: Record<TeamShareMeasure, string> = {
  territories: 'Territories',
  troops: 'Troops',
  income: 'Income',
}

/** Bar height, as in the mockup. */
const SEGMENT_HEIGHT = 26

export function TeamShareBar({ measures, sharesFor, viewerTeam }: {
  /** The measures the viewer may compare (fog hides some). Non-empty. */
  measures: readonly TeamShareMeasure[]
  /** Each team's total for a measure. */
  sharesFor: (measure: TeamShareMeasure) => TeamShare[]
  viewerTeam: TeamId | null
}) {
  const [picked, setPicked] = useState<TeamShareMeasure>('territories')
  const measure = measures.includes(picked) ? picked : measures[0]!
  // Letter order, so a team keeps its place as the lead changes.
  const shares = [...sharesFor(measure)].sort((a, b) => TEAM_IDS.indexOf(a.team) - TEAM_IDS.indexOf(b.team))
  const total = shares.reduce((sum, s) => sum + s.value, 0)
  const summary = teamShareSummary(shares, viewerTeam)
  return (
    <View testID="team-share-bar" className="border border-olive rounded-lg p-2" style={{ gap: 6 }}>
      <View className="flex-row items-center justify-between" style={{ gap: 8 }}>
        <Text className="text-ink font-stencil text-xs uppercase tracking-widest" accessibilityLabel={`Team share: ${MEASURE_LABEL[measure].toLowerCase()}`}>
          {measures.length > 1 ? 'Team share' : MEASURE_LABEL[measure]}
        </Text>
        {measures.length > 1 && (
          <View className="flex-row" style={{ gap: 4 }} accessibilityRole="radiogroup" accessibilityLabel="Share bar measure">
            {measures.map((m) => {
              const on = m === measure
              return (
                <TouchableOpacity
                  key={m}
                  testID={`team-share-measure-${m}`}
                  onPress={() => setPicked(m)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: on }}
                  accessibilityLabel={`Compare ${MEASURE_LABEL[m].toLowerCase()}`}
                  hitSlop={{ top: 8, bottom: 8, left: 2, right: 2 }}
                  className={`rounded-full px-2 border ${on ? 'bg-olive border-olive' : 'border-olive/60'}`}
                  style={{ paddingVertical: 2 }}
                >
                  <Text className={`font-typed text-[11px] ${on ? 'text-paper' : 'text-ink-soft'}`}>{MEASURE_LABEL[m].toLowerCase()}</Text>
                </TouchableOpacity>
              )
            })}
          </View>
        )}
      </View>
      <View
        className="flex-row rounded-md overflow-hidden"
        style={{ height: SEGMENT_HEIGHT, gap: 2 }}
        accessibilityRole="image"
        accessibilityLabel={`${MEASURE_LABEL[measure]}: ${shares.map((s) => `${teamLabel(s.team)} ${s.value}`).join(', ')}`}
      >
        {shares.map((s) => (
          <View
            key={s.team}
            testID={`team-share-${s.team}`}
            style={{
              // Every team keeps a labelled slice, even at zero.
              flexGrow: total > 0 ? Math.max(s.value, 0) : 1,
              flexBasis: 0,
              minWidth: 46,
              backgroundColor: TEAM_TINTS[s.team],
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <TeamShape team={s.team} size={13} fill={TEAM_TINT_INK} />
            <Text style={{ color: TEAM_TINT_INK, fontFamily: 'Inter', fontWeight: '700', fontSize: 11 }}>{s.team}</Text>
            <Text testID={`team-share-${s.team}-value`} style={{ color: TEAM_TINT_INK, fontFamily: 'Inter', fontSize: 11 }}>{s.value}</Text>
          </View>
        ))}
      </View>
      {!!summary && <Text testID="team-share-summary" className="text-ink-soft font-typed text-xs">{summary}</Text>}
    </View>
  )
}
